import React, { useEffect, useMemo, useState } from 'react';
import {
  PiCheckDuotone,
  PiMagnifyingGlassDuotone,
  PiPaperPlaneTiltDuotone,
  PiTrashDuotone,
  PiUserCirclePlusDuotone,
  PiUsersDuotone,
  PiXDuotone,
} from 'react-icons/pi';
import {
  acceptFriendRequest,
  cancelFriendRequest,
  declineFriendRequest,
  getFriendRequests,
  getFriends,
  removeFriend,
  searchUsers,
  sendFriendRequest,
} from '../api/friendService';
import Button from '../shared/common/Button';
import Card from '../shared/common/Card';
import EmptyState from '../shared/common/EmptyState';
import Input from '../shared/common/Input';
import { publicUserInitial, publicUserName, safeUsername } from '../shared/utils/userDisplay';

const MIN_SEARCH_LENGTH = 2;
const STATUS_LABELS = {
  PENDING: 'Заявка отправлена',
  ACCEPTED: 'Уже в друзьях',
  DECLINED: 'Заявка отклонена',
  CANCELLED: 'Заявка отменена',
};

export default function FriendsPage() {
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState({ incoming: [], outgoing: [] });
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState('');

  const canSearch = useMemo(() => query.trim().length >= MIN_SEARCH_LENGTH, [query]);
  const incomingRequests = requests.incoming || [];
  const outgoingRequests = requests.outgoing || [];
  const incomingRequestCount = incomingRequests.length;
  const outgoingRequestCount = outgoingRequests.length;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [friendsData, requestsData] = await Promise.all([getFriends(), getFriendRequests()]);
      setFriends(friendsData);
      setRequests(requestsData);
    } catch {
      setError('Не удалось загрузить список друзей');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (event) => {
    event.preventDefault();
    if (!canSearch) return;

    setError('');
    setActionLoading('search');
    try {
      setSearchResults(await searchUsers(query.trim()));
    } catch (err) {
      setError(err.response?.data?.message || 'Не удалось выполнить поиск');
    } finally {
      setActionLoading(null);
    }
  };

  const runAction = async (key, action) => {
    setActionLoading(key);
    setError('');
    try {
      await action();
      await loadData();
      if (canSearch) {
        setSearchResults(await searchUsers(query.trim()));
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Не удалось выполнить действие');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="bm-friends-page bm-animate-in">
      <header className="bm-friends-hero">
        <div>
          <h1 className="bm-h2">Друзья</h1>
Твой круг для быстрых приглашений в комнаты и дружеские пари.
        </div>
        <div className="bm-friends-hero__stats" aria-label="Сводка по друзьям и заявкам">
          <StatPill label="Друзей" value={friends.length} />
          {incomingRequestCount > 0 && <StatPill label="Входящих" value={incomingRequestCount} accent />}
          {outgoingRequestCount > 0 && <StatPill label="Исходящих" value={outgoingRequestCount} />}
        </div>
      </header>

      {error && <div className="bm-auth-error">{error}</div>}

      <div className="bm-friends-layout">
        <main className="bm-friends-layout__main">
          <section className="bm-friends-section">
            <div className="bm-friends-section__header">
              <div>
                <p className="bm-eyebrow">Главное</p>
                <h2 className="bm-h3">Мои друзья</h2>
              </div>
              <PiUsersDuotone className="bm-friends-section__icon" size={28} aria-hidden="true" />
            </div>

            {loading ? (
              <p className="bm-muted">Загружаем друзей…</p>
            ) : friends.length ? (
              <div className="bm-friends-list">
                {friends.map((friend) => (
                  <ProfileRow
                    key={friend.id}
                    profile={friend}
                    right={
                      <Button
                        size="sm"
                        variant="danger"
                        icon={<PiTrashDuotone size={16} />}
                        loading={actionLoading === `remove-${friend.id}`}
                        onClick={() => runAction(`remove-${friend.id}`, () => removeFriend(friend.id))}
                      >
                        Удалить
                      </Button>
                    }
                  />
                ))}
              </div>
            ) : (
              <EmptyState title="Список друзей пуст" hint="Найди пользователя справа и отправь заявку — принятые друзья появятся здесь первыми." />
            )}
          </section>
        </main>

        <aside className="bm-friends-layout__side">
          <Card className="bm-friends-search-card">
            <div className="bm-friends-section__header bm-friends-section__header--compact">
              <div>
                <p className="bm-eyebrow">Добавление</p>
                <h2 className="bm-h3">Найти пользователя</h2>
              </div>
              <PiUserCirclePlusDuotone className="bm-friends-section__icon" size={26} aria-hidden="true" />
            </div>
            <form onSubmit={handleSearch} className="bm-friends-search-form">
              <Input
                label="Поиск пользователя"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Введите username или имя"
              />
              <Button
                type="submit"
                icon={<PiMagnifyingGlassDuotone size={16} />}
                loading={actionLoading === 'search'}
                disabled={!canSearch}
              >
                Найти
              </Button>
            </form>
            <p className="bm-muted bm-friends-search-card__hint">
              Минимум {MIN_SEARCH_LENGTH} символа. Email и другие приватные данные не показываются.
            </p>
          </Card>

          {searchResults.length > 0 && (
            <section className="bm-friends-section bm-friends-section--compact">
              <h2 className="bm-h3">Результаты поиска</h2>
              <div className="bm-friends-list bm-friends-list--compact">
                {searchResults.map((profile) => (
                  <ProfileRow
                    key={profile.id}
                    profile={profile}
                    right={
                      profile.friendshipStatus === 'ACCEPTED' || profile.friendshipStatus === 'PENDING' ? (
                        <span className="bm-friends-status">{STATUS_LABELS[profile.friendshipStatus]}</span>
                      ) : (
                        <Button
                          size="sm"
                          icon={<PiPaperPlaneTiltDuotone size={16} />}
                          loading={actionLoading === `send-${profile.id}`}
                          onClick={() => runAction(`send-${profile.id}`, () => sendFriendRequest(profile.id))}
                        >
                          Добавить
                        </Button>
                      )
                    }
                  />
                ))}
              </div>
            </section>
          )}

          {incomingRequestCount > 0 && (
            <section className="bm-friends-section bm-friends-section--compact">
              <div className="bm-friends-section__header bm-friends-section__header--compact">
                <h2 className="bm-h3">Входящие заявки</h2>
                <span className="bm-friends-badge">{incomingRequestCount}</span>
              </div>
              <div className="bm-friends-list bm-friends-list--compact">
                {incomingRequests.map((request) => (
                  <ProfileRow
                    key={request.id}
                    profile={request.requester}
                    right={
                      <div className="bm-friends-actions">
                        <Button
                          size="sm"
                          variant="success"
                          icon={<PiCheckDuotone size={16} />}
                          loading={actionLoading === `accept-${request.id}`}
                          onClick={() => runAction(`accept-${request.id}`, () => acceptFriendRequest(request.id))}
                        >
                          Принять
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          icon={<PiXDuotone size={16} />}
                          loading={actionLoading === `decline-${request.id}`}
                          onClick={() => runAction(`decline-${request.id}`, () => declineFriendRequest(request.id))}
                        >
                          Отклонить
                        </Button>
                      </div>
                    }
                  />
                ))}
              </div>
            </section>
          )}

          {outgoingRequestCount > 0 && (
            <section className="bm-friends-section bm-friends-section--compact">
              <div className="bm-friends-section__header bm-friends-section__header--compact">
                <h2 className="bm-h3">Исходящие заявки</h2>
                <span className="bm-friends-badge">{outgoingRequestCount}</span>
              </div>
              <div className="bm-friends-list bm-friends-list--compact">
                {outgoingRequests.map((request) => (
                  <ProfileRow
                    key={request.id}
                    profile={request.recipient}
                    right={
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={<PiXDuotone size={16} />}
                        loading={actionLoading === `cancel-${request.id}`}
                        onClick={() => runAction(`cancel-${request.id}`, () => cancelFriendRequest(request.id))}
                      >
                        Отменить
                      </Button>
                    }
                  />
                ))}
              </div>
            </section>
          )}
        </aside>
      </div>
    </div>
  );
}

function StatPill({ label, value, accent = false }) {
  return (
    <div className={`bm-friends-stat${accent ? ' bm-friends-stat--accent' : ''}`}>
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

function ProfileRow({ profile, right }) {
  return (
    <Card className="bm-profile-row-card">
      <div className="bm-profile-row">
        <div className="bm-profile-row__identity">
          <div className="bm-sidebar__avatar">{publicUserInitial(profile)}</div>
          <div className="bm-profile-row__text">
            <strong>{publicUserName(profile)}</strong>
            {safeUsername(profile) && <div className="bm-muted">@{safeUsername(profile)}</div>}
          </div>
        </div>
        <div className="bm-profile-row__action">{right}</div>
      </div>
    </Card>
  );
}
